import type { RequirementRow, RequirementSeries } from '@api/replenishment-engine';

import { addDaysKey } from './dates';

/**
 * The stock curve the engine reads, re-drawn on the page: inbound counts from the start of
 * the day it lands, demand is consumed through the day, and a stockout loses the sales (the
 * curve floors at zero). Same rules as the engine's `project`, so a number typed here is
 * read the way the next run will read it.
 */
export const projectStock = (onHand: number, demand: number[], inbound: number[]): { atStart: number[]; close: number[] } => {
    const atStart: number[] = [];
    const close: number[] = [];
    let carry = onHand;
    for (let d = 0; d < demand.length; d += 1) {
        const start = carry + (inbound[d] ?? 0);
        atStart.push(start);
        carry = Math.max(0, start - (demand[d] ?? 0));
        close.push(carry);
    }
    return { atStart, close };
};

/** Days of demand `stock` covers from day `d` on; past the curve's end, the last days' rate carries on. */
export const coverDaysFrom = (stock: number, demand: number[], d: number): number => {
    let left = stock;
    let days = 0;
    for (let i = d; i < demand.length; i += 1) {
        const need = demand[i] ?? 0;
        if (need <= 0) {
            days += 1;
            continue;
        }
        if (left < need) return days + left / need;
        left -= need;
        days += 1;
    }
    const tail = demand.slice(-14).filter((v) => v > 0);
    const rate = tail.length ? tail.reduce((s, v) => s + v, 0) / tail.length : 0;
    return rate > 0 ? days + left / rate : days;
};

export const dayIndex = (from: string, day: string | null | undefined): number | null => {
    if (!day) return null;
    const ms = new Date(`${day}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime();
    return Math.round(ms / 86400000);
};

/** One day of the story, every number in units. */
export interface StoryDay {
    day: string;
    demand: number;
    inTransit: number;
    inProduction: number;
    /** Approved plan lines landing (in the inbound, neither in transit nor in production yet). */
    onBook: number;
    proposed: number;
    /** Stock at the start of the day, without what this plan proposes. */
    without: number;
    /** Stock at the start of the day, with it. */
    withPlan: number;
    /** The level (L) and refill (R) lines in units, from the day's demand rate. */
    level: number;
    refill: number;
}

/**
 * The days of the curve, from today, with what is landing split by kind and the curve drawn
 * with and without the plan's landings. `extra` adds landings on top of the proposals, e.g.
 * a number typed in the table and not yet re-planned.
 */
export const buildStory = (args: {
    series: RequirementSeries;
    onHand: number;
    levelDays: number;
    refillDays: number;
    /** The proposals to draw: each part's quantity on the day it lands. */
    landings: Array<{ day: string | null; qty: number }>;
    days: number;
}): StoryDay[] => {
    const { series, onHand, levelDays, refillDays, landings, days } = args;
    const len = Math.min(days, series.demand.length);
    const demand = series.demand.slice(0, series.demand.length);
    const base = series.inbound.slice(0, series.demand.length);
    const proposed = new Array<number>(series.demand.length).fill(0);
    for (const l of landings) {
        const i = dayIndex(series.from, l.day);
        if (i != null && i >= 0 && i < proposed.length) proposed[i] += l.qty;
    }
    const without = projectStock(onHand, demand, base).atStart;
    const withPlan = projectStock(
        onHand,
        demand,
        base.map((v, i) => v + proposed[i]),
    ).atStart;
    // The day's rate for L and R: a 7-day average forward, so a single heavy shipment day does not spike the lines.
    const rateAt = (i: number): number => {
        const slice = demand.slice(i, i + 7);
        return slice.length ? slice.reduce((s, v) => s + v, 0) / slice.length : 0;
    };
    const out: StoryDay[] = [];
    for (let i = 0; i < len; i += 1) {
        const inTransit = series.inTransit?.[i] ?? 0;
        const inProduction = series.inProduction?.[i] ?? 0;
        const rate = rateAt(i);
        out.push({
            day: addDaysKey(series.from, i),
            demand: demand[i] ?? 0,
            inTransit,
            inProduction,
            onBook: Math.max(0, (base[i] ?? 0) - inTransit - inProduction),
            proposed: proposed[i],
            without: without[i],
            withPlan: withPlan[i],
            level: rate * levelDays,
            refill: rate * refillDays,
        });
    }
    return out;
};

/**
 * Days of cover a dip lands to with `qty` on its landing day: the SKU's curve, with the
 * landings of its earlier dips on it (`before`), read from the landing day forward.
 */
export const coverAfter = (args: { series: RequirementSeries | null; onHand: number; before: Array<{ day: string | null; qty: number }>; landDate: string | null; qty: number }): number | null => {
    const { series: s, onHand, before, landDate, qty } = args;
    if (!s || !landDate) return null;
    const i = dayIndex(s.from, landDate);
    if (i == null || i < 0 || i >= s.demand.length) return null;
    const inbound = s.inbound.slice();
    for (const b of before) {
        const j = dayIndex(s.from, b.day);
        if (j != null && j >= 0 && j < inbound.length) inbound[j] += b.qty;
    }
    const { atStart } = projectStock(onHand, s.demand, inbound);
    return coverDaysFrom(atStart[i] + qty, s.demand, i);
};

/** The series a SKU's rows carry (on the first dip's head), and the landings of each dip in seq order. */
export const curveOf = (rows: RequirementRow[]): RequirementSeries | null => rows.find((r) => r.series)?.series ?? null;
